import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { filterVariants, type GenomicVariant } from '@/lib/genomics';
import { AdminPage, RegistryTable, FeedbackControl } from '@/admin';

const REGISTRY = 'genomic_variants';
const TIER_LABEL: Record<string, string> = { normal: 'Normal', borderline: 'Borderline', high: 'High' };

function Interpretations({ v }: { v: GenomicVariant }) {
  const entries = Object.entries(v.interpretations ?? {});
  if (!entries.length) return <span className="text-text-muted">—</span>;
  return (
    <div className="space-y-1">
      {entries.map(([tier, text]) => (
        <div key={tier} className="text-[11px] leading-snug">
          <span className="font-semibold text-text-secondary">{TIER_LABEL[tier] ?? tier}:</span>{' '}
          <span className="text-text-secondary">{text.length > 120 ? `${text.slice(0, 119)}…` : text}</span>
        </div>
      ))}
    </div>
  );
}

export function GenomicsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['genomics'], queryFn: () => adminApi.genomics.list() });
  const fb = useQuery({ queryKey: ['feedback-summary', REGISTRY], queryFn: () => feedbackApi.summary(REGISTRY) });
  const [search, setSearch] = useState('');

  const all = q.data?.variants ?? [];
  const rows = filterVariants(all, search);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', REGISTRY] });

  return (
    <AdminPage
      title="Genomic interpretations (SNPs)"
      description="The curated single-nucleotide-polymorphism knowledge base the DNA-ingestion pipeline reads — one entry per rsID: gene, reference strand, risk allele, and the per-genotype narrative. Read-only (authored via PR); flag an entry for Medical Advisory review."
    >
      {q.data ? <div className="mb-13 text-body-sm text-text-secondary">{all.length} variants across {new Set(all.map((v) => v.gene)).size} genes</div> : null}
      <RegistryTable<GenomicVariant>
        columns={[
          { key: 'rsid', header: 'rsID', className: 'font-mono text-[12px]', render: (v) => (
            <div>
              <div>{v.rsid}</div>
              {(v.merged_rsids ?? []).length ? <div className="text-[10px] text-text-muted">merged: {v.merged_rsids!.join(', ')}</div> : null}
            </div>
          ) },
          { key: 'gene', header: 'Gene', className: 'font-semibold', render: (v) => v.gene },
          { key: 'variant', header: 'Variant', className: 'font-mono text-[11px] text-text-secondary', render: (v) => v.variant_tag || '—' },
          { key: 'allele', header: 'Risk → wild', className: 'font-mono text-[12px]', render: (v) => `${(v.risk_alleles ?? []).join('/') || '?'} → ${v.wild_type || '?'} (${v.strand || '?'})` },
          { key: 'rule', header: 'Rule', className: 'font-mono text-[11px] text-text-secondary', render: (v) => v.interpretation_rule || '—' },
          { key: 'interp', header: 'Interpretations', className: 'max-w-[420px]', render: (v) => <Interpretations v={v} /> },
        ]}
        rows={rows}
        getRowKey={(v) => v.rsid}
        count={all.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={search ? `No variants match "${search}"` : 'No variants.'}
        search={{ value: search, onChange: setSearch, placeholder: 'Search by rsID, gene, or variant…' }}
        rowActions={(v) => <FeedbackControl registry={REGISTRY} itemId={v.rsid} itemLabel={`${v.gene} ${v.rsid}`} openCount={fb.data?.[v.rsid]?.open ?? 0} onChanged={refresh} />}
      />
    </AdminPage>
  );
}
